import { getInstanceDefinition } from '../cards/cardLookup';

export function getActivePlayer(state) {
  return state.players[state.meta.activePlayerIndex];
}

export function getPlayerById(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  if (!player) throw new Error(`Unknown player id: ${playerId}`);
  return player;
}

// Caverns of Bane, Troll Skullcracker's Raid obligation (see
// effectRegistry/monsters.js's own registration for the full mechanic) -
// shared between actions/index.js (blocks Visit/Prepare/Rest) and
// actions/dungeonActions.js (restricts which monster may be fought) here to
// avoid a circular import between the two. Void (not blocking) if the
// targeted monster is no longer in the hall by the time it's actually this
// player's turn - nothing left to hold them to.
export function hasActiveForcedFightObligation(state, playerId) {
  const obligation = state.meta.forcedFightObligation;
  return Boolean(obligation) && obligation.playerId === playerId && state.dungeonHall.includes(obligation.monsterInstanceId);
}

// Root of Corruption, Mind Control (see effectRegistry/villages.js) - a
// monster instance sitting in player.inPlayInstanceIds (the shared "in
// front of you" zone - state/createGameState.js) is unambiguously an
// assigned-monster obligation, distinguishable from the OTHER things that
// can live there (the familiar, Circle of Protection copies) purely by its
// own card type. No hall-membership check needed: the assigned monster was
// deliberately removed from the hall when it was assigned, so its
// continued existence lives in this array, not in dungeonHall.
export function getAssignedMonsterInstanceId(state, playerId) {
  const player = getPlayerById(state, playerId);
  return player.inPlayInstanceIds.find((id) => getInstanceDefinition(state, id).type === 'monster');
}

// Shared here (not actions/index.js or actions/dungeonActions.js) to avoid
// a circular import between those two files - same reasoning as
// hasActiveForcedFightObligation above.
export function hasAssignedMonsterObligation(state, playerId) {
  return Boolean(getAssignedMonsterInstanceId(state, playerId));
}

// Live-recalculated gold value of a player's current hand (rulebook: recomputed
// dynamically as cards are destroyed/added mid-Village-action, never
// snapshotted at reveal time).
export function computeHandGoldValue(state, playerId) {
  const player = getPlayerById(state, playerId);
  const cardGold = player.hand.reduce((sum, instanceId) => {
    const def = getInstanceDefinition(state, instanceId);
    return sum + (def.goldValue || 0);
  }, 0);
  // bonusGold: ability-granted gold (e.g. "Gain 2 gold") that isn't tied to a
  // card's own printed goldValue - see engine/state/turnFlags.js.
  return Math.max(0, cardGold + (player.turnFlags.bonusGold || 0));
}

// Gold left to spend this Village visit: the turn's total budget minus
// whatever a prior purchase (or bonus buy) already committed - see
// turnFlags.goldSpentThisVisit. Distinct from computeHandGoldValue, which is
// the full per-turn total and doesn't reflect purchases already made.
export function computeRemainingGold(state, playerId) {
  const player = getPlayerById(state, playerId);
  return Math.max(0, computeHandGoldValue(state, playerId) - (player.turnFlags.goldSpentThisVisit || 0));
}

export function getHallMonster(state, hallRankIndex) {
  const instanceId = state.dungeonHall[hallRankIndex];
  return instanceId ? getInstanceDefinition(state, instanceId) : null;
}

// Every card instance a player currently owns, across every zone at once
// (hand + deck + discard + familiar/Circle-of-Protection-in-play) -
// "everything I've ever bought and still have" (destroyed cards are gone
// for good, not part of this). Equipped weapons need no special handling:
// equipWeapon only records a heroInstanceId -> weaponInstanceId mapping, it
// never removes the weapon from `hand` (see dungeonActions.equipWeapon) -
// so hand already covers them. inPlayInstanceIds holds a MIX of
// player-owned cards (the familiar while "out"; Circle of Protection
// copies) and merely-assigned ones (Mind Control's monster is never owned
// by this player, just temporarily sitting in front of them) - monster-type
// entries are excluded here, same disambiguation as
// getAssignedMonsterInstanceId. When the familiar ISN'T currently in play,
// that same instance is already sitting in hand/deck/discard and including
// it again here would double it (same reasoning as scoring.js's
// computePlayerScore, which uses this selector too).
export function getPlayerCollectionInstanceIds(state, playerId) {
  const player = getPlayerById(state, playerId);
  return [
    ...player.hand,
    ...player.deck,
    ...player.discard,
    ...player.inPlayInstanceIds.filter((id) => getInstanceDefinition(state, id).type !== 'monster'),
    // Into the Abyss, Grim (all 4 levels): cards set aside for the rest of
    // the game (cards/effectHelpers.js's setAsideForGame) - unlike
    // inPlayInstanceIds above, monster-type entries here ARE included
    // (Mind Control's own exclusion reasoning doesn't apply - a set-aside
    // monster is owned/scored by this player, not merely assigned).
    ...player.setAsideInstanceIds,
  ];
}
